import { useSetPageTitle } from "libs/ga"
import {
  ChartSection,
  MealTypeBreakdownSection,
  NavButton,
  NoDataText,
  OverviewBgOverlay,
  OverviewContent,
  OverviewHeader,
  OverviewMain,
  PeriodButton,
  StyledDay,
  StyledOverview,
  TimeController,
  TodayButton,
  WaterBottomSheet,
  WaterTabContent,
} from "./styled"
import { StyledDatePickerPopup } from "components/Common/StyledPopup"
import { FlexBox } from "components/Common/FlexBox"
import { BackButton } from "components/Common/BackButton"
import DropdownDownIcon from "components/Icons/DropdownDownIcon"
import { useDateSearchParam, useResetWeeklyDataOnUnmount, useWeeklyOverview, useWeeklyWater } from "../hooks"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import DatePicker from "components/pickers/DatePicker"
import MealTypePercent from "./MealTypePercent"
import MostMeals, {
  TopFoodsHeader,
  TopFoodsTitle,
  PeriodText,
} from "./MostMeals"
import { IOverviewTab, IWaterHistoryItem } from "../types"
import WeeklyBarChart, { getYDomain } from "./WeeklyBarChart"
import CustomTooltip from "./WeeklyBarChart/Tooltip"
import WaterWeeklyBarChart, { getWaterMaxYAxis } from "./WaterWeeklyBarChart"
import WaterTooltip from "./WaterWeeklyBarChart/Tooltip"
import WaterHistory from "./WaterHistory"
import SummaryCards from "./SummaryCards"
import DraggableBottomSheet from "components/Common/DraggableBottomSheet"
import dayjs, { Dayjs } from "dayjs"
import {
  US_DATE_FORMAT_SHORT,
  US_DATE_FORMAT_WITH_YEAR,
  MONTH_DAY_FORMAT,
  DAY_YEAR_FORMAT,
  MONTH_DAY_YEAR_FORMAT,
} from "config"
import RoundedSwitch from "components/inputs/RoundedSwitch"
import NavigateLeftCircleIcon from "components/Icons/NavigateLeftCircleIcon"
import NavigateRightCircleIcon from "components/Icons/NavigateRightCircleIcon"
import useShowFitBanner from "features/notification/useShowFitBanner"
import { useBackToHome } from "features/meal/hooks"
import {
  computeDayMealTypePercent,
  getEmptyFoodChartData,
  getEmptyWaterChartData,
} from "../helper"
import { useBodyDisplayUnits } from "features/profile/hooks/body"
import { getDisplayWaterVolume, getWaterUnit } from "utils/waterVolumeConverter"

const getWeeklyPeriodText = (date: Dayjs) => {
  const startOfWeek = date.startOf("week")
  const endOfWeek = date.endOf("week")

  const isSameYear = startOfWeek.isSame(endOfWeek, "year")

  if (!isSameYear) {
    return `${startOfWeek.format(US_DATE_FORMAT_SHORT)} - ${endOfWeek.format(US_DATE_FORMAT_SHORT)}`
  }

  const isSameMonth = startOfWeek.isSame(endOfWeek, "month")

  if (isSameMonth) {
    return `${startOfWeek.format(MONTH_DAY_FORMAT)} - ${endOfWeek.format(DAY_YEAR_FORMAT)}`
  }

  return `${startOfWeek.format(MONTH_DAY_FORMAT)} - ${endOfWeek.format(MONTH_DAY_YEAR_FORMAT)}`
}

const Overview = () => {
  const [activeTab, setActiveTab] = useState<IOverviewTab>("food")
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [isBottomSheetExpanded, setIsBottomSheetExpanded] = useState(false)

  useSetPageTitle("Overview")
  useResetWeeklyDataOnUnmount()
  const [showCalendar, setShowCalendar] = useState(false)
  const {
    date,
    onDateChange,
    dateString,
    onGoToNextWeek,
    onGoToPreviousWeek,
  } = useDateSearchParam()

  const { weeklyOverview, getWeeklyOverviewLoading } =
    useWeeklyOverview(dateString)

  const { weeklyWater, getWeeklyWaterLoading } = useWeeklyWater(dateString)
  const { bodyDisplayUnits } = useBodyDisplayUnits()
  const waterUnit = getWaterUnit(bodyDisplayUnits)

  const numberOfDaysHasData = weeklyOverview?.weeklyChartData.filter(
    (item) =>
      item.breakfast > 0 ||
      item.lunch > 0 ||
      item.dinner > 0 ||
      item.snack > 0 ||
      item.other > 0,
  ).length

  const maxYAxis = (() => {
    if (!weeklyOverview || !numberOfDaysHasData) return 0
    const avgCal = weeklyOverview.gainedCalories / numberOfDaysHasData
    const goalCalories =
      weeklyOverview.weeklyChartData[weeklyOverview.weeklyChartData.length - 1]
        .goalCalories
    const [, max] = getYDomain(
      weeklyOverview.weeklyChartData,
      goalCalories,
      avgCal,
    )
    return max
  })()

  const convertedWaterChartData = useMemo(
    () =>
      weeklyWater?.weeklyChartData.map((item) => ({
        ...item,
        amount: getDisplayWaterVolume(bodyDisplayUnits, item.amount),
      })),
    [weeklyWater, bodyDisplayUnits],
  )

  const currentPeriodWaterHistory = useMemo((): IWaterHistoryItem[] => {
    const weekStart = date.startOf("week")
    return Array.from({ length: 7 }, (_, i) => {
      const day = weekStart.add(6 - i, "day")
      return {
        date: day.format(US_DATE_FORMAT_WITH_YEAR),
        amount: 0,
      }
    })
  }, [date])

  const waterMaxYAxis = convertedWaterChartData
    ? getWaterMaxYAxis(convertedWaterChartData)
    : 0

  const loading =
    activeTab === "food" ? getWeeklyOverviewLoading : getWeeklyWaterLoading

  const foodAnimationLoading = getWeeklyOverviewLoading
  const waterAnimationLoading = getWeeklyWaterLoading

  const isThisWeek = date.isSame(dayjs(), "week")
  const dateTitle = getWeeklyPeriodText(date)

  const hasFoodData = weeklyOverview && weeklyOverview.gainedCalories > 0
  const hasWaterData = weeklyWater && weeklyWater.weeklyTotal > 0

  const { shouldShowFitBanner } = useShowFitBanner()
  const backToHome = useBackToHome()

  const [tabSwitching, setTabSwitching] = useState(false)

  const onTabChange = (tab: IOverviewTab) => {
    if (activeTab !== tab) {
      setActiveTab(tab)
      setTabSwitching(true)
      setActiveIndex(null)
      setIsBottomSheetExpanded(false)
    }
  }

  useEffect(() => {
    if (tabSwitching) {
      const timer = setTimeout(() => setTabSwitching(false), 50)
      return () => clearTimeout(timer)
    }
  }, [tabSwitching])

  // Derive active data from weekly chart data
  const foodActiveData =
    activeIndex !== null
      ? weeklyOverview?.weeklyChartData[activeIndex] ?? null
      : null

  const waterActiveData =
    activeIndex !== null
      ? convertedWaterChartData?.[activeIndex] ?? null
      : null

  // Reset bar selection when navigating to a different week
  useEffect(() => {
    setActiveIndex(null)
  }, [dateString])

  const handleBarClick = useCallback((index: number | null) => {
    setActiveIndex(index)
  }, [])

  const handleDeselect = useCallback(() => {
    setActiveIndex(null)
  }, [])

  const handleTodayClick = useCallback(() => {
    onDateChange(dayjs())
    setActiveIndex(null)
  }, [onDateChange])

  return (
    <StyledOverview
      style={
        {
          "--fit-banner-height": shouldShowFitBanner ? "58px" : "0px",
        } as React.CSSProperties
      }
    >
      <OverviewBgOverlay $height={275} />
      <StyledDatePickerPopup
        position="top"
        visible={showCalendar}
        onMaskClick={() => setShowCalendar(false)}
      >
        <DatePicker
          onChange={(date) => {
            onDateChange(date)
            setShowCalendar(false)
          }}
          value={date}
          period="weekly"
        />
      </StyledDatePickerPopup>

      <OverviewHeader>
        <BackButton
          style={{
            position: "absolute",
            left: 12,
            top: 4,
            width: 32,
            height: 32,
          }}
          onClick={backToHome}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M8.29279 12.7064C8.10532 12.5188 8 12.2645 8 11.9994C8 11.7342 8.10532 11.4799 8.29279 11.2924L13.9498 5.63537C14.042 5.53986 14.1524 5.46367 14.2744 5.41126C14.3964 5.35886 14.5276 5.33127 14.6604 5.33012C14.7932 5.32896 14.9248 5.35426 15.0477 5.40454C15.1706 5.45483 15.2823 5.52908 15.3762 5.62297C15.4701 5.71686 15.5443 5.82852 15.5946 5.95141C15.6449 6.07431 15.6702 6.20599 15.669 6.33877C15.6679 6.47155 15.6403 6.60277 15.5879 6.72477C15.5355 6.84677 15.4593 6.95712 15.3638 7.04937L10.4138 11.9994L15.3638 16.9494C15.5459 17.138 15.6467 17.3906 15.6445 17.6528C15.6422 17.915 15.537 18.1658 15.3516 18.3512C15.1662 18.5366 14.9154 18.6418 14.6532 18.644C14.391 18.6463 14.1384 18.5455 13.9498 18.3634L8.29279 12.7064Z"
              fill="white"
            />
          </svg>
        </BackButton>

        <FlexBox
          style={{ width: "100%", height: 44 }}
          $justify="center"
          $alignItems="center"
        >
          <RoundedSwitch
            options={[
              {
                label: "Food",
                value: "food",
              },
              {
                label: "Water",
                value: "water",
              },
            ]}
            value={activeTab}
            onChange={(value) => onTabChange(value as IOverviewTab)}
          />
        </FlexBox>
      </OverviewHeader>

      <OverviewMain onClick={handleDeselect}>
        <OverviewContent>
          {activeTab === "food" && (
            <>
              {foodActiveData && activeIndex !== null ? (
                <CustomTooltip
                  activeData={foodActiveData}
                  barIndex={activeIndex}
                  unit="cal"
                  maxYAxis={maxYAxis}
                />
              ) : (
                <SummaryCards
                  weeklyTotal={weeklyOverview?.gainedCalories ?? 0}
                  avgDaily={
                    numberOfDaysHasData
                      ? (weeklyOverview?.gainedCalories ?? 0) /
                        numberOfDaysHasData
                      : 0
                  }
                  unit="cal"
                  loading={foodAnimationLoading}
                />
              )}

              <ChartSection onClick={(e) => e.stopPropagation()}>
                <FlexBox
                  style={{
                    width: "100%",
                    height: 154,
                  }}
                >
                  <WeeklyBarChart
                    averageGainedCaloriesPerDay={
                      numberOfDaysHasData
                        ? (weeklyOverview?.gainedCalories ?? 0) /
                          numberOfDaysHasData
                        : 0
                    }
                    data={
                      getWeeklyOverviewLoading
                        ? getEmptyFoodChartData(date)
                        : (weeklyOverview?.weeklyChartData ??
                          getEmptyFoodChartData(date))
                    }
                    activeIndex={activeIndex}
                    onBarClick={handleBarClick}
                  />
                </FlexBox>

                <TimeController>
                  <NavButton onClick={onGoToPreviousWeek}>
                    <NavigateLeftCircleIcon size={32} />
                  </NavButton>
                  <PeriodButton onClick={() => setShowCalendar(true)}>
                    <StyledDay>{dateTitle}</StyledDay>
                    <DropdownDownIcon fill="white" />
                  </PeriodButton>
                  <TodayButton
                    $disabled={isThisWeek}
                    onClick={handleTodayClick}
                  >
                    Today
                  </TodayButton>
                  <NavButton onClick={onGoToNextWeek}>
                    <NavigateRightCircleIcon size={32} />
                  </NavButton>
                </TimeController>
              </ChartSection>

              {hasFoodData && weeklyOverview && (
                <>
                  <MealTypeBreakdownSection>
                    <MealTypePercent
                      mealTypePercent={
                        foodActiveData
                          ? computeDayMealTypePercent(foodActiveData)
                          : weeklyOverview.mealTypePercent
                      }
                      loading={foodAnimationLoading}
                      disableAnimation={tabSwitching}
                    />
                  </MealTypeBreakdownSection>

                  <DraggableBottomSheet
                    peekHeight={114}
                    expanded={isBottomSheetExpanded}
                    onExpandChange={(expanded) => {
                      setIsBottomSheetExpanded(expanded)
                      setActiveIndex(null)
                    }}
                    header={
                      <TopFoodsHeader>
                        <TopFoodsTitle>Top Foods</TopFoodsTitle>
                        {isBottomSheetExpanded && (
                          <PeriodText>
                            ({getWeeklyPeriodText(date)})
                          </PeriodText>
                        )}
                      </TopFoodsHeader>
                    }
                  >
                    <MostMeals mostMeals={weeklyOverview.mostMeals} />
                  </DraggableBottomSheet>
                </>
              )}

              {!hasFoodData && !loading && (
                <NoDataText>
                  Nothing happened during these dates. Try a different time
                  period
                </NoDataText>
              )}
            </>
          )}

          {activeTab === "water" && (
            <WaterTabContent>
              {waterActiveData && activeIndex !== null ? (
                <WaterTooltip
                  activeData={waterActiveData}
                  barIndex={activeIndex}
                  unit={waterUnit}
                  maxYAxis={waterMaxYAxis}
                />
              ) : (
                <SummaryCards
                  weeklyTotal={getDisplayWaterVolume(
                    bodyDisplayUnits,
                    weeklyWater?.weeklyTotal ?? 0,
                  )}
                  avgDaily={getDisplayWaterVolume(
                    bodyDisplayUnits,
                    weeklyWater?.avgDaily ?? 0,
                  )}
                  unit={waterUnit}
                  loading={waterAnimationLoading}
                />
              )}

              <ChartSection onClick={(e) => e.stopPropagation()}>
                <FlexBox
                  style={{
                    width: "100%",
                    height: 154,
                  }}
                >
                  <WaterWeeklyBarChart
                    data={
                      getWeeklyWaterLoading
                        ? getEmptyWaterChartData(date)
                        : (convertedWaterChartData ??
                          getEmptyWaterChartData(date))
                    }
                    activeIndex={activeIndex}
                    onBarClick={handleBarClick}
                  />
                </FlexBox>

                <TimeController>
                  <NavButton onClick={onGoToPreviousWeek}>
                    <NavigateLeftCircleIcon size={32} />
                  </NavButton>
                  <PeriodButton onClick={() => setShowCalendar(true)}>
                    <StyledDay>{dateTitle}</StyledDay>
                    <DropdownDownIcon fill="white" />
                  </PeriodButton>
                  <TodayButton
                    $disabled={isThisWeek}
                    onClick={handleTodayClick}
                  >
                    Today
                  </TodayButton>
                  <NavButton onClick={onGoToNextWeek}>
                    <NavigateRightCircleIcon size={32} />
                  </NavButton>
                </TimeController>
              </ChartSection>

              {hasWaterData && weeklyWater ? (
                <WaterBottomSheet>
                  <WaterHistory
                    waterHistory={weeklyWater?.waterHistory ?? currentPeriodWaterHistory}
                    selectedDate={waterActiveData?.date ?? null}
                    loading={waterAnimationLoading}
                    disableAnimation={tabSwitching}
                    bodyDisplayUnits={bodyDisplayUnits}
                  />
                </WaterBottomSheet>
              ) : (
                !loading && (
                  <NoDataText>
                    Nothing happened during these dates. Try a different time
                    period
                  </NoDataText>
                )
              )}
            </WaterTabContent>
          )}
        </OverviewContent>
      </OverviewMain>
    </StyledOverview>
  )
}

export default Overview
